import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { salesService } from '../../../services/salesService';
import { expenseService } from '../../../services/expenseService';

const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n);

const FinanceHubHeader: React.FC = () => {
  const [sales, setSales] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - 30); // Last 30 days for header
        const [s, e] = await Promise.all([
          salesService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
          expenseService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
        ]);
        setSales(s);
        setExpenses(e);
      } catch (err) {
        console.error('Header metrics load error', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { revenue, spend, profit } = useMemo(() => {
    const revenue = sales.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const spend = expenses.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const profit = revenue - spend;
    return { revenue, spend, profit };
  }, [sales, expenses]);

  return (
    <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        {/* Title & Description */}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Finance Hub</h1>
            <Link
              to="/help?article=revenue-operations-transactions"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Track sales, expenses, and get AI-powered financial insights — all in one place
          </p>
        </div>

        {/* Quick Metrics */}
        {loading ? (
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>Loading metrics...</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <span className="text-lg">💰</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Revenue (30d)</div>
                <div className="text-base font-semibold text-gray-900">{fmt(revenue)}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <span className="text-lg">💸</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Expenses (30d)</div>
                <div className="text-base font-semibold text-gray-900">{fmt(spend)}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                profit >= 0 ? 'bg-blue-100' : 'bg-orange-100'
              }`}>
                <span className="text-lg">{profit >= 0 ? '📈' : '📉'}</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Net Profit (30d)</div>
                <div className={`text-base font-semibold ${
                  profit >= 0 ? 'text-blue-600' : 'text-orange-600'
                }`}>
                  {fmt(profit)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceHubHeader;
